import { Box, Grid, Paper, TextField, MenuItem, Button } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"; // make sure to install @mui/x-date-pickers
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import { PtsAlertRequest } from "./PtsAlertRequest";
import { PtsAlertResponse } from "./PtsAlertResponse";
import Layout from "../../components/Layout";
import dayjs from "dayjs";
import { searchGridSx } from "../../utils/responsiveStyles";

export default function Alert() {
  // 🔹 Table state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);

  // 🔹 Map frontend field names → backend DB names
  const orderByMap = {
    id: "id",
    dateTime: "date_time",
    deviceType: "device_type",
    deviceNumber: "device_number",
    state: "state",
    codeDescription: "code_description",
  };

  // 🔹 Dropdown options (from API)
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [codes, setCodes] = useState([]);

  // 🔹 Search filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [deviceType, setDeviceType] = useState("");
  const [deviceNumber, setDeviceNumber] = useState("");
  const [state, setState] = useState("");
  const [code, setCode] = useState("");

  useEffect(() => {
    axiosClient
      .get("/alert/get-device-type-lookup") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device types: " + JSON.stringify(data));
        setDeviceTypes(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));

    axiosClient
      .get("/alert/get-device-state-lookup") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device state: " + JSON.stringify(data));
        setStates(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));
  }, []);

  // 🔹 Fetch Data
  const fetchData = () => {
    const token = localStorage.getItem("token");
    console.log("token: " + token);
    setLoading(true);

    const request = new PtsAlertRequest();
    request.setStartIndex(page * pageSize);
    request.setLimit(pageSize);

    if (sortModel.length > 0) {
      const sort = sortModel[0]; // { field: "codeDescription", sort: "asc" }
      const backendField = orderByMap[sort.field] || sort.field;
      console.log(`order by ${backendField} ${sort.sort?.toUpperCase()}`);
      request.setOrderBy(
        `order by ${backendField} ${sort.sort?.toUpperCase()}`,
      );
    }

    if (startDate) request.setStartDate(startDate.toISOString());
    if (endDate) request.setEndDate(endDate.toISOString());
    if (deviceType) request.setDeviceType(deviceType);
    if (deviceNumber) request.setDeviceNumber(Number(deviceNumber));
    if (state) request.setState(state);
    if (code) request.setCode(Number(code));

    axiosClient
      .post("/alert/get-all-pts-alert", request)
      .then((res) => {
        const ptsResponse = new PtsAlertResponse(res.data);
        if (
          ptsResponse?.getOperationResult() &&
          ptsResponse.getPtsAlertList()
        ) {
          setRows(ptsResponse.getPtsAlertList());
          setRowCount(ptsResponse.getCount());
        } else {
          setRows([]);
          setRowCount(0);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  // initial load + refetch when page/pageSize changes
  useEffect(() => {
    console.log(page, pageSize);
    fetchData();
  }, [page, pageSize, sortModel]);

  // 🔹 Columns
  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    {
      field: "dateTime",
      headerName: "Date Time",
      flex: 1,
      renderCell: (params) => {
        const raw = params.row?.dateTime;
        return raw ? dayjs(raw).format("DD/MM/YYYY HH:mm:ss") : "";
      },
    },
    { field: "deviceType", headerName: "Device Type", width: 150 },
    { field: "deviceNumber", headerName: "Device Number", width: 200 },
    { field: "state", headerName: "State", width: 200 },
    { field: "codeDescription", headerName: "Code", width: 200 },
  ];

  const handleDeviceType = (value) => {
    setDeviceType(value);
    if (value === "") {
      setCode("");
      setCodes([]);
    } else {
      axiosClient
        .get("/alert/get-device-alert-lookup-by-device-type/" + value) // ✅ your backend endpoint for dropdowns
        .then((res) => {
          const data = res.data;
          console.log("device codes: " + JSON.stringify(data));
          setCodes(data || []);
        })
        .catch((err) => console.error("Dropdown fetch failed", err));
    }
  };

  return (
    <Layout title="Alert">
      {/* Search Form */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container sx={searchGridSx}>
          {/* search fields ... */}
          <Grid item xs={12} sm={6} md={3}>
            <DateTimePicker
              label="Start Date"
              value={startDate}
              onChange={(newVal) => setStartDate(newVal)}
              sx={{ width: "100%" }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DateTimePicker
              label="End Date"
              value={endDate}
              onChange={(newVal) => setEndDate(newVal)}
              sx={{ width: "100%" }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Device Type"
              value={deviceType}
              onChange={(e) => handleDeviceType(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {deviceTypes.map((dt, i) => (
                <MenuItem key={i} value={dt}>
                  {dt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Device Number"
              value={deviceNumber}
              onChange={(e) => setDeviceNumber(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {[0, 1, 2, 3, 4, 5].map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {states.map((dt, i) => (
                <MenuItem key={i} value={dt}>
                  {dt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(codes).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
              onClick={fetchData}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid */}
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          sortingOrder={["asc", "desc"]}
          loading={loading}
          pageSizeOptions={[5, 10, 20, 50]}
          autoHeight
          sx={{ minWidth: { xs: 900, sm: "100%" }, bgcolor: "white" }}
        />
      </Box>
    </Layout>
  );
}
